import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

import TextField from "@material-ui/core/TextField";
import { makeStyles, type Theme } from "@material-ui/core/styles";

import Autocomplete from "src/components/Autocomplete";
import { AddGeneralLedgerAccountDialog } from "src/pages/RootDialogs/AddGeneralLedgerAccountDialog";
import { generalLedgerAccountsSelector } from "src/store/glAccounts/selector";
import {
  generalLedgerAccountTypeToLabel,
  GeneralLedgerAccountTypeOrderById,
} from "src/utils/general-ledger-accounts";
import type { GeneralLedgerAccountV2 } from "src/interfaces/generalLedgerAccount.interface";

const ADD_NEW_ACCOUNT_ID = "__add_new_account__";

const useStyles = makeStyles((theme: Theme) => ({
  option: {
    padding: "0 !important",
  },
  optionContent: {
    padding: theme.spacing(0.75, 3),
    width: "100%",
  },
}));

export interface GLAccountAutocompleteProps {
  value?: string | null;
  onChange: (account: GeneralLedgerAccountV2 | null) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  margin?: "none" | "dense" | "normal";
  /**
   * Filter applied to the list of GL accounts before rendering.
   * Return false to hide the account from the dropdown.
   */
  filter?: (account: GeneralLedgerAccountV2) => boolean;
  autoFocus?: boolean;
  /**
   * When true (default), show an "Add New Account" option that opens the
   * add-gl-account dialog when selected.
   */
  showAddNewAccount?: boolean;
}

/**
 * Simple, reusable GL account autocomplete. Unlike the bespoke version in the
 * main transaction form, this one doesn't handle balance-transfer or inventory
 * side effects, but it does offer the shared "Add New Account" option.
 *
 * "Add New Account" opens a locally-owned add-account dialog (rather than the
 * global `add_gl_account` root dialog), so the component works inside other
 * dialogs and autofills the newly created account back into the field.
 */
export default function GLAccountAutocomplete({
  value,
  onChange,
  label = "General Ledger Account",
  disabled,
  required,
  error,
  helperText,
  margin = "dense",
  filter,
  autoFocus,
  showAddNewAccount = true,
}: GLAccountAutocompleteProps) {
  const classes = useStyles();
  const [addOpen, setAddOpen] = useState(false);
  const generalLedgerAccounts = useSelector(generalLedgerAccountsSelector);

  const options = useMemo(() => {
    const accounts = generalLedgerAccounts.filter((a) => {
      if (!a) return false;
      if (a.system) return false;
      if (a.deleted && a.id !== value) return false;
      if (filter && !filter(a)) return false;
      return true;
    });

    accounts.sort(
      (a, b) =>
        GeneralLedgerAccountTypeOrderById[a.type[0]] -
          GeneralLedgerAccountTypeOrderById[b.type[0]] ||
        a.name.localeCompare(b.name)
    );

    if (showAddNewAccount) {
      accounts.unshift({
        name: "Add New Account",
        type: ["new"],
        id: ADD_NEW_ACCOUNT_ID,
      } as any);
    }

    return accounts;
  }, [generalLedgerAccounts, filter, value, showAddNewAccount]);

  return (
    <>
      <AddGeneralLedgerAccountDialog
        open={addOpen}
        onClose={(account) => {
          setAddOpen(false);
          if (account) onChange(account);
        }}
      />
      <Autocomplete
        fullWidth
        disabled={disabled}
        options={options}
        classes={{ option: classes.option }}
        value={(value ?? null) as any}
        onChange={(_event, option: any) => {
          if (option?.id === ADD_NEW_ACCOUNT_ID) {
            setAddOpen(true);
            return;
          }
          onChange(option ?? null);
        }}
        autoHighlight
        getOptionLabel={(option: any) =>
          option?.name ||
          options.find((opt) => opt.id === option)?.name ||
          options.find((opt) => opt.name === option)?.name ||
          ""
        }
        getOptionSelected={(option: any, selected: any) =>
          option.id === selected || option.name === selected
        }
        groupBy={(option: any) =>
          option.id === ADD_NEW_ACCOUNT_ID
            ? ""
            : generalLedgerAccountTypeToLabel(option.type)[0]
        }
        renderOption={(option: any, { inputValue }: any) => {
          const matches = match(option.name, inputValue);
          const parts = parse(option.name, matches);
          return (
            <div className={clsx(classes.optionContent)}>
              {parts.map((part: any, index: number) => (
                <span
                  id={option.id}
                  key={index}
                  style={{ fontWeight: part.highlight ? 700 : 400 }}
                >
                  {part.text}
                </span>
              ))}
            </div>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            error={error}
            helperText={helperText}
            margin={margin}
            required={required}
            label={label}
            fullWidth
            variant="outlined"
            autoFocus={autoFocus}
          />
        )}
      />
    </>
  );
}
